import { Component, type ErrorInfo, type ReactNode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import widgetCss from './widget.css?inline'
import type { WidgetConfig } from '../types/widget'
import { applyDocumentLang, getSupportedLanguage } from '../utils/chatUiLocale'
import { VoiceNavWidget } from './VoiceNavWidget'
import { applyVoiceNavMountHostStyles, showVoiceNavMountError } from './mountHostStyles'

type MountResult = {
  destroy: () => void
}

class VoiceNavWidgetErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[VoiceNavWidget] Render failed:', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="vnw-widgetRoot" data-position="bottom-right">
          <div className="vnw-mountRenderError" role="alert">
            Voice assistant failed to load. Check the browser console, then refresh the page.
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export function mountVoiceNavWidget(config: WidgetConfig): MountResult {
  applyDocumentLang(getSupportedLanguage())

  const targetEl = document.querySelector(config.target) as HTMLElement | null
  if (!targetEl) {
    const msg =
      `Voice assistant mount failed: element "${config.target}" was not found on this page.`
    showVoiceNavMountError(msg)
    throw new Error(
      `[VoiceNavWidget] Target element not found for selector: "${config.target}". ` +
        `Create it in your HTML (e.g. <div id="voice-nav-root"></div>).`,
    )
  }

  applyVoiceNavMountHostStyles(targetEl)

  const shadowRoot = targetEl.shadowRoot ?? targetEl.attachShadow({ mode: 'open' })

  // Inject isolated styles once.
  const styleId = 'vnw-widget-style'
  if (!shadowRoot.getElementById(styleId)) {
    const styleEl = document.createElement('style')
    styleEl.id = styleId
    styleEl.textContent = widgetCss
    shadowRoot.appendChild(styleEl)
  }

  // Create a fresh React mount point each time `init()` is called.
  const mountSelector = '[data-vnw-react-mount]'
  const existingMount = shadowRoot.querySelector(mountSelector) as HTMLDivElement | null
  if (existingMount) existingMount.remove()

  const reactMount = document.createElement('div')
  reactMount.setAttribute('data-vnw-react-mount', 'true')
  shadowRoot.appendChild(reactMount)

  const root: Root = createRoot(reactMount)
  root.render(
    <VoiceNavWidgetErrorBoundary>
      <VoiceNavWidget config={config} />
    </VoiceNavWidgetErrorBoundary>,
  )

  return {
    destroy: () => {
      try {
        root.unmount()
      } catch {
        // Ignore.
      }
      reactMount.remove()
    },
  }
}

